import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { InputComponent } from '../../../shared/input/input.component';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell.component';
import { AppService } from '../../../services/application/app.service';
import { CreateApplicationPayload } from '../../../interfaces/application';
import { Subject, takeUntil } from 'rxjs';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';
import { resolveErrorMessage } from '../../../shared/utils/api-error.util';
import { APP_DESCRIPTION_MAX_LENGTH, APP_NAME_MAX_LENGTH } from '../shared-form-elements';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'app-add-application-modal',
  standalone: true,
  imports: [CommonModule, InputComponent, FormsModule, ReactiveFormsModule, MatFormFieldModule, ModalShellComponent],
  templateUrl: './add-application-modal.component.html',
  styleUrl: './add-application-modal.component.css',
})
export class AddApplicationModalComponent implements OnInit, OnDestroy {
  applicationForm!: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<AddApplicationModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
    },
    private fb: FormBuilder,
    private appService: AppService,
    private messageModal: MessageModalService
  ) {}

  isErrorVisible: boolean = false;
  errorMessage: string = '';

  private destroy$ = new Subject<void>();

  appNameLabel = $localize`Application name`;
  appNamePlaceholder = $localize`Enter Application name`;
  appNameMaxLength = APP_NAME_MAX_LENGTH;
  descriptionLabel = $localize`Description`;
  descriptionPlaceholder = $localize`Enter Description`;
  descriptionMaxLength = APP_DESCRIPTION_MAX_LENGTH;

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    this.applicationForm = this.fb.group({
      applicationName: ['', [Validators.required, Validators.maxLength(APP_NAME_MAX_LENGTH), noWhitespaceValidator()]],
      description: ['', [Validators.maxLength(APP_DESCRIPTION_MAX_LENGTH), noWhitespaceValidator()]],
    });
  }

  closePopup(): void {
    this.dialogRef.close();
  }

  handleSubmit(): void {
    this.isErrorVisible = false;

    if (this.applicationForm.invalid) {
      this.applicationForm.markAllAsTouched();
      return;
    }

    const { applicationName, description } = this.applicationForm.value;

    const payload: CreateApplicationPayload = {
      name: applicationName,
    };

    if (description) {
      payload.description = description;
    }

    this.appService
      .createApp(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.dialogRef.close({ success: true, action: 'add' });
          } else {
            this.handleApiError($localize`${response.message}`, $localize`Error while creating the Application.`);
          }
        },
        error: (err: any) => {
          console.error('Error while creating the Application:', err);
          this.messageModal.openModal($localize`Error`, $localize`Error while creating the Application.`);
        },
      });
  }

  // Helper function for error handling
  private handleApiError(error: any, fallbackMessage: string): void {
    this.errorMessage = resolveErrorMessage(error, fallbackMessage);
    this.isErrorVisible = true;
  }

  get applicationName() {
    return this.applicationForm.get('applicationName');
  }

  get description() {
    return this.applicationForm.get('description');
  }

  isFormChanged(): boolean {
    return this.applicationForm.dirty;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
